import React, { useState, useEffect } from "react";
import {
  Modal,
  Card,
  Row,
  Col,
  Form,
  Button,
  Alert,
  Spinner,
} from "react-bootstrap";
import {
  IconPalette,
  IconX,
  IconCheck,
  IconUpload,
  IconEye,
  IconRefresh,
  IconColorPicker,
} from "@tabler/icons-react";
import Swal from "sweetalert2";
import OrganizationService from "../services/OrganizationServices";

// Gradientes pré-definidos
const PRESET_GRADIENTS = [
  {
    name: "Primary",
    value: "var(--gradient-primary)",
    color1: "#305CDE",
    color2: "#1ea7c5"
  },
  {
    name: "Success",
    value: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
    color1: "#10b981",
    color2: "#059669"
  },
  {
    name: "Danger",
    value: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
    color1: "#ef4444",
    color2: "#dc2626"
  },
  {
    name: "Warning",
    value: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
    color1: "#f59e0b",
    color2: "#d97706"
  },
  {
    name: "Info",
    value: "linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)",
    color1: "#06b6d4",
    color2: "#0891b2"
  },
  {
    name: "Neutral",
    value: "linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)",
    color1: "#f8fafc",
    color2: "#e2e8f0"
  },
  {
    name: "Highlight",
    value: "var(--gradient-primary)",
    color1: "#305cde",
    color2: "#1ea7c5"
  },
  {
    name: "Pink",
    value: "linear-gradient(135deg, #d64c9d 0%, #e83e8c 100%)",
    color1: "#d64c9d",
    color2: "#e83e8c"
  }
];

const OrganizationBranding = ({ show, onHide, organizationData }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    primaryColor: "#305CDE",
    secondaryColor: "#1ea7c5",
    textColor: "#000000",
    gradientColor1: "#305CDE",
    gradientColor2: "#1ea7c5",
    selectedGradient: "",
    logo: null,
    logoMobile: null,
    favicon: null,
    customDomain: "",
    siteTitle: "",
    siteDescription: "",
  });
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoMobilePreview, setLogoMobilePreview] = useState(null);
  const [faviconPreview, setFaviconPreview] = useState(null);

  useEffect(() => {
    if (organizationData) {
      setFormData({
        primaryColor: organizationData.primary_color || "#305CDE",
        secondaryColor: organizationData.titles_color || "#1ea7c5",
        textColor: organizationData.text_color || "#000000",
        gradientColor1: organizationData.gradient_color1 || "#305CDE",
        gradientColor2: organizationData.gradient_color2 || "#1ea7c5",
        selectedGradient: organizationData.selected_gradient || "",
        logo: null,
        logoMobile: null,
        favicon: null,
        customDomain: organizationData.custom_domain || "",
        siteTitle: organizationData.site_title || "",
        siteDescription: organizationData.site_description || "",
      });
      
      // Definir previews de imagens existentes
      if (organizationData.logo) setLogoPreview(organizationData.logo);
      if (organizationData.logo_mobile) setLogoMobilePreview(organizationData.logo_mobile);
      if (organizationData.favicon) setFaviconPreview(organizationData.favicon);
    }
  }, [organizationData]);

  const handleColorChange = (colorField, value) => {
    setFormData((prev) => ({
      ...prev,
      [colorField]: value,
    }));
  };

  const handleFileUpload = (fileType, event) => {
    const file = event.target.files[0];
    if (file) {
      // Validar arquivo antes de processar
      const validation = validateFile(file, fileType);
      if (!validation.isValid) {
        Swal.fire({
          icon: "error",
          title: "Arquivo Inválido",
          text: validation.errors.join(", "),
        });
        return;
      }

      if (fileType === "logo") {
        setFormData((prev) => ({ ...prev, logo: file }));
        const reader = new FileReader();
        reader.onload = (e) => setLogoPreview(e.target.result);
        reader.readAsDataURL(file);
      } else if (fileType === "logoMobile") {
        setFormData((prev) => ({ ...prev, logoMobile: file }));
        const reader = new FileReader();
        reader.onload = (e) => setLogoMobilePreview(e.target.result);
        reader.readAsDataURL(file);
      } else if (fileType === "favicon") {
        setFormData((prev) => ({ ...prev, favicon: file }));
        const reader = new FileReader();
        reader.onload = (e) => setFaviconPreview(e.target.result);
        reader.readAsDataURL(file);
      }
    }
  };

  const validateFile = (file, type) => {
    const errors = [];
    const allowedTypes = {
      logo: ["image/jpeg", "image/png", "image/svg+xml"],
      logoMobile: ["image/jpeg", "image/png", "image/svg+xml"],
      favicon: ["image/x-icon", "image/png"],
    };
    const maxSizes = {
      logo: 2 * 1024 * 1024, // 2MB
      logoMobile: 2 * 1024 * 1024, // 2MB
      favicon: 100 * 1024, // 100KB
    };

    if (!allowedTypes[type].includes(file.type)) {
      errors.push(`Formato não suportado para ${type}`);
    }

    if (file.size > maxSizes[type]) {
      const maxSizeText = type === "favicon" ? "100KB" : "2MB";
      errors.push(`Arquivo muito grande. Máximo: ${maxSizeText}`);
    }

    return { isValid: errors.length === 0, errors };
  };

  const resetColors = () => {
    setFormData((prev) => ({
      ...prev,
      primaryColor: "#305CDE",
      secondaryColor: "#1ea7c5",
      textColor: "#000000",
      gradientColor1: "#305CDE",
      gradientColor2: "#1ea7c5",
      selectedGradient: "",
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Preparar dados para envio
      const gradientPrimary = generateGradient(formData.gradientColor1, formData.gradientColor2);
      
      const dataToSend = {
        primary_color: formData.primaryColor,
        titles_color: formData.secondaryColor,
        text_color: formData.textColor,
        gradient_primary: gradientPrimary,
        gradient_color1: formData.gradientColor1,
        gradient_color2: formData.gradientColor2,
        selected_gradient: formData.selectedGradient,
        custom_domain: formData.customDomain,
        site_title: formData.siteTitle,
        site_description: formData.siteDescription,
      };

      // Adicionar apenas arquivos que foram realmente alterados pelo usuário
      if (formData.logo instanceof File) {
        dataToSend.logo = formData.logo;
      }
      if (formData.logoMobile instanceof File) {
        dataToSend.logo_mobile = formData.logoMobile;
      }
      if (formData.favicon instanceof File) {
        dataToSend.favicon = formData.favicon;
      }

      // Validar dados antes de enviar
      const validation = OrganizationService.validateBrandingData(dataToSend);
      if (!validation.isValid) {
        const errorMessages = Object.values(validation.errors).join(", ");
        throw new Error(errorMessages);
      }

      // Detectar se já existe branding (baseado na presença de cores ou logos existentes)
      const hasExistingBranding = organizationData && (
        organizationData.primary_color || 
        organizationData.logo || 
        organizationData.logo_mobile || 
        organizationData.favicon ||
        organizationData.custom_domain
      );

      // Usar OrganizationService para enviar
      const response = await OrganizationService.updateBranding(organizationData.id, dataToSend, hasExistingBranding);
      
      // Atualizar cache do WhitelabelContext com os novos dados
      const cacheUpdated = OrganizationService.updateWhitelabelCache(response);
      
      if (cacheUpdated) {
        // Disparar evento customizado para o WhitelabelContext
        window.dispatchEvent(new CustomEvent('whitelabel-cache-updated', {
          detail: { source: 'organization-branding', cacheKey: 'whitelabel_config' }
        }));
        
        // Recarregar a página para garantir que as mudanças sejam aplicadas
        setTimeout(() => {
          window.location.reload();
        }, 3000);
      }
      
      await new Promise(resolve => setTimeout(resolve, 2000)); // Simulação

      Swal.fire({
        icon: "success",
        title: "Branding Atualizado!",
        text: "As configurações de branding foram salvas com sucesso. A página será recarregada para aplicar as mudanças.",
        confirmButtonColor: formData.primaryColor,
        showConfirmButton: false,
        timer: 2500
      });
      onHide();
    } catch (error) {
      console.error("Erro ao salvar branding:", error);
      Swal.fire({
        icon: "error",
        title: "Erro",
        text: error.response?.data?.message || "Não foi possível salvar as configurações de branding.",
        confirmButtonColor: "#dc3545",
      });
    } finally {
      setLoading(false);
    }
  };

  // Função para gerar gradiente baseado nas cores selecionadas
  const generateGradient = (color1, color2) => {
    return `linear-gradient(135deg, ${color1} 0%, ${color2} 100%)`;
  };

  // Função para lidar com seleção de gradiente pré-definido
  const handlePresetGradientSelect = (gradient) => {
    setFormData((prev) => ({
      ...prev,
      gradientColor1: gradient.color1,
      gradientColor2: gradient.color2,
      selectedGradient: gradient.name,
    }));
  };

  const GradientPicker = ({ label, color1, color2, selectedPreset, onChange, onPresetSelect, helpText }) => {
    const currentGradient = generateGradient(color1, color2);

    return (
      <Col md={12} className="mb-3">
        <Form.Label className="fw-semibold d-flex align-items-center">
          <IconPalette size={16} className="me-2" />
          {label}
        </Form.Label>
        
        {/* Gradientes Pré-definidos */}
        <div className="mb-3">
          <small className="text-muted mb-2 d-block">Gradientes Pré-definidos:</small>
          <div className="d-flex flex-wrap gap-2">
            {PRESET_GRADIENTS.map((gradient) => (
              <div
                key={gradient.name}
                className={`gradient-preset ${selectedPreset === gradient.name ? 'selected' : ''}`}
                onClick={() => onPresetSelect(gradient)}
                style={{
                  width: "40px",
                  height: "40px",
                  background: gradient.value,
                  borderRadius: "8px",
                  cursor: "pointer",
                  border: selectedPreset === gradient.name ? "3px solid #305CDE" : "2px solid #dee2e6",
                  boxShadow: selectedPreset === gradient.name ? "0 0 0 2px rgba(48, 92, 222, 0.2)" : "none",
                  transition: "all 0.2s ease"
                }}
                title={gradient.name}
              />
            ))}
          </div>
        </div>

        {/* Seletores de Cor Personalizados */}
        <div className="mb-3">
          <small className="text-muted mb-2 d-block">Cores Personalizadas:</small>
          <Row>
            <Col md={6}>
              <Form.Label className="small">Cor 1:</Form.Label>
              <div className="d-flex align-items-center gap-2">
                <Form.Control
                  type="color"
                  value={color1}
                  onChange={(e) => onChange('gradientColor1', e.target.value)}
                  style={{ 
                    width: "40px", 
                    height: "32px", 
                    padding: "2px",
                    cursor: "pointer",
                    border: "2px solid #dee2e6"
                  }}
                />
                <Form.Control
                  type="text"
                  value={color1}
                  onChange={(e) => onChange('gradientColor1', e.target.value)}
                  placeholder="#000000"
                  style={{ fontFamily: "monospace", fontSize: "0.875rem" }}
                />
              </div>
            </Col>
            <Col md={6}>
              <Form.Label className="small">Cor 2:</Form.Label>
              <div className="d-flex align-items-center gap-2">
                <Form.Control
                  type="color"
                  value={color2}
                  onChange={(e) => onChange('gradientColor2', e.target.value)}
                  style={{ 
                    width: "40px", 
                    height: "32px", 
                    padding: "2px",
                    cursor: "pointer",
                    border: "2px solid #dee2e6"
                  }}
                />
                <Form.Control
                  type="text"
                  value={color2}
                  onChange={(e) => onChange('gradientColor2', e.target.value)}
                  placeholder="#000000"
                  style={{ fontFamily: "monospace", fontSize: "0.875rem" }}
                />
              </div>
            </Col>
          </Row>
        </div>

        {/* Preview do Gradiente */}
        <div className="mb-2">
          <small className="text-muted mb-1 d-block">Preview:</small>
          <div
            className="rounded"
            style={{
              width: "100%",
              height: "40px",
              background: currentGradient,
              border: "1px solid #dee2e6",
              boxShadow: "inset 0 1px 2px rgba(0,0,0,0.1)"
            }}
          />
        </div>

        {helpText && (
          <Form.Text className="text-muted small">{helpText}</Form.Text>
        )}
      </Col>
    );
  };

  const ColorPicker = ({ label, value, onChange, helpText }) => {
    return (
      <Col md={6} className="mb-3">
        <Form.Label className="fw-semibold d-flex align-items-center">
          <IconColorPicker size={16} className="me-2" />
          {label}
        </Form.Label>
        <div className="d-flex align-items-center gap-2">
          <Form.Control
            type="color"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            style={{ 
              width: "50px", 
              height: "38px", 
              padding: "2px",
              cursor: "pointer",
              border: "2px solid #dee2e6"
            }}
          />
          <Form.Control
            type="text"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder="#000000"
            pattern="^#[0-9A-Fa-f]{6}$"
            style={{ fontFamily: "monospace" }}
          />
        </div>
        {helpText && (
          <Form.Text className="text-muted small">{helpText}</Form.Text>
        )}
        <div
          className="mt-2 rounded"
          style={{
            width: "100%",
            height: "20px",
            backgroundColor: value,
            border: "1px solid #dee2e6",
          }}
        />
      </Col>
    );
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton className="border-0 pb-0">
        <Modal.Title className="d-flex align-items-center">
          <IconPalette size={24} className="me-2 text-primary" />
          Personalização da Organização
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="px-4 pb-4">
        <Form onSubmit={handleSubmit}>
          {/* Seção de Cores */}
          <Card className="mb-4 border-0 shadow-sm">
            <Card.Header className="bg-light border-0">
              <h6 className="mb-0 fw-bold d-flex align-items-center">
                <IconPalette size={18} className="me-2" />
                Cores da Marca
                <Button
                  variant="outline-secondary"
                  size="sm"
                  className="ms-auto"
                  onClick={resetColors}
                >
                  <IconRefresh size={14} className="me-1" />
                  Resetar
                </Button>
              </h6>
            </Card.Header>
            <Card.Body>
              <Row>
                <ColorPicker
                  label="Cor Primária"
                  value={formData.primaryColor}
                  onChange={(value) => handleColorChange("primaryColor", value)}
                  helpText="Cor principal da interface"
                />
                <ColorPicker
                  label="Cor de Títulos"
                  value={formData.secondaryColor}
                  onChange={(value) =>
                    handleColorChange("secondaryColor", value)
                  }
                  helpText="Cor dos títulos e textos de destaque"
                />
              </Row>

              <Row>
                <ColorPicker
                  label="Cor do Texto"
                  value={formData.textColor}
                  onChange={(value) => handleColorChange("textColor", value)}
                  helpText="Cor padrão do texto"
                />
              </Row>

              <Row>
                <GradientPicker
                  label="Gradiente Primário"
                  color1={formData.gradientColor1}
                  color2={formData.gradientColor2}
                  selectedPreset={formData.selectedGradient}
                  onChange={handleColorChange}
                  onPresetSelect={handlePresetGradientSelect}
                  helpText="Gradiente usado em botões e elementos de destaque"
                />
              </Row>
            </Card.Body>
          </Card>

          {/* Seção de Logotipo */}
          <Card className="mb-4 border-0 shadow-sm">
            <Card.Header className="bg-light border-0">
              <h6 className="mb-0 fw-bold d-flex align-items-center">
                <IconUpload size={18} className="me-2" />
                Logotipo e Favicon
              </h6>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={4}>
                  <Form.Label className="fw-semibold">
                    Logo Principal
                  </Form.Label>
                  <Form.Control
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleFileUpload("logo", e)}
                    className="mb-2"
                  />
                  <Form.Text className="text-muted">
                    PNG/SVG, máx. 2MB, 200x60px
                  </Form.Text>
                  {logoPreview && (
                    <div className="mt-3 p-3 border rounded text-center bg-light">
                      <img
                        src={logoPreview}
                        alt="Preview do Logo"
                        style={{ maxHeight: "60px", maxWidth: "200px" }}
                      />
                    </div>
                  )}
                </Col>
                <Col md={4}>
                  <Form.Label className="fw-semibold">Logo Mobile</Form.Label>
                  <Form.Control
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleFileUpload("logoMobile", e)}
                    className="mb-2"
                  />
                  <Form.Text className="text-muted">
                    PNG/SVG, máx. 2MB, 150x50px
                  </Form.Text>
                  {logoMobilePreview && (
                    <div className="mt-3 p-3 border rounded text-center bg-light">
                      <img
                        src={logoMobilePreview}
                        alt="Preview do Logo Mobile"
                        style={{ maxHeight: "50px", maxWidth: "150px" }}
                      />
                    </div>
                  )}
                </Col>
                <Col md={4}>
                  <Form.Label className="fw-semibold">Favicon</Form.Label>
                  <Form.Control
                    type="file"
                    accept=".ico,.png"
                    onChange={(e) => handleFileUpload("favicon", e)}
                    className="mb-2"
                  />
                  <Form.Text className="text-muted">
                    ICO/PNG, máx. 100KB, 32x32px
                  </Form.Text>
                  {faviconPreview && (
                    <div className="mt-3 p-3 border rounded text-center bg-light">
                      <img
                        src={faviconPreview}
                        alt="Preview do Favicon"
                        style={{ width: "32px", height: "32px" }}
                      />
                    </div>
                  )}
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Seção de SEO/Meta Tags */}
          <Card className="mb-4 border-0 shadow-sm">
            <Card.Header className="bg-light border-0">
              <h6 className="mb-0 fw-bold d-flex align-items-center">
                <IconCheck size={18} className="me-2" />
                SEO & Meta Tags
              </h6>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-semibold">
                      Título do Site
                    </Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.siteTitle}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          siteTitle: e.target.value,
                        }))
                      }
                      placeholder="Minha Plataforma - Agentes de IA"
                      maxLength="60"
                    />
                    <Form.Text className="text-muted">
                      Título que aparecerá na aba do navegador e redes sociais (máx. 60 caracteres)
                    </Form.Text>
                  </Form.Group>
                </Col>
                <Col md={12}>
                  <Form.Group>
                    <Form.Label className="fw-semibold">
                      Descrição do Site
                    </Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      value={formData.siteDescription}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          siteDescription: e.target.value,
                        }))
                      }
                      placeholder="Descrição que aparecerá nos resultados de busca e redes sociais"
                      maxLength="160"
                    />
                    <Form.Text className="text-muted">
                      Descrição para motores de busca e compartilhamento (máx. 160 caracteres)
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Seção de Domínio */}
          <Card className="mb-4 border-0 shadow-sm">
            <Card.Header className="bg-light border-0">
              <h6 className="mb-0 fw-bold d-flex align-items-center">
                <IconCheck size={18} className="me-2" />
                Domínio Personalizado
                {/* <Badge bg="warning" className="ms-2">
                  Premium
                </Badge> */}
              </h6>
            </Card.Header>
            <Card.Body>
              <Form.Group>
                <Form.Label className="fw-semibold">
                  Domínio Customizado (Opcional)
                </Form.Label>
                <Form.Control
                  type="url"
                  value={formData.customDomain}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      customDomain: e.target.value,
                    }))
                  }
                  placeholder="https://meudominio.com"
                />
                <Form.Text className="text-muted">
                  Configure um domínio próprio para sua organização. Esta
                  funcionalidade requer configuração DNS adicional.
                </Form.Text>
              </Form.Group>
            </Card.Body>
          </Card>

          {/* Preview Section */}
          <Card className="mb-4 border-0 shadow-sm">
            <Card.Header className="bg-light border-0">
              <h6 className="mb-0 fw-bold d-flex align-items-center">
                <IconEye size={18} className="me-2" />
                Preview das Cores
              </h6>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={6}>
                  <div
                    className="p-3 rounded bg-light"
                    style={{
                      border: `2px solid ${formData.primaryColor}`,
                      minHeight: "120px",
                    }}
                  >
                    <div className="mb-3">
                      <Button
                        size="sm"
                        style={{
                          backgroundColor: formData.primaryColor,
                          borderColor: formData.primaryColor,
                        }}
                      >
                        Botão Primário
                      </Button>
                      <Button
                        size="sm"
                        variant="outline-secondary"
                        className="ms-2"
                        style={{
                          color: formData.secondaryColor,
                          borderColor: formData.secondaryColor,
                        }}
                      >
                        Secundário
                      </Button>
                    </div>
                    <div className="mt-3">
                      <span
                        style={{
                          color: formData.primaryColor,
                          fontWeight: "bold",
                        }}
                      >
                        Texto com cor primária
                      </span>
                      <br />
                      <span
                        style={{
                          color: formData.secondaryColor,
                        }}
                      >
                        Texto com cor secundária
                      </span>
                      <br />
                      <span
                        style={{
                          color: formData.textColor,
                        }}
                      >
                        Texto com cor padrão
                      </span>
                    </div>
                  </div>
                </Col>
                <Col md={6}>
                  <Alert
                    variant="info"
                    className="border-0 h-100 d-flex align-items-center"
                  >
                    <div>
                      <strong>Informações:</strong>
                      <ul className="mb-0 mt-2">
                        <li>As alterações serão aplicadas após salvar</li>
                        <li>
                          Pode levar alguns minutos para aparecer em toda
                          plataforma
                        </li>
                        <li>O preview mostra como as cores ficarão</li>
                      </ul>
                    </div>
                  </Alert>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Form>
      </Modal.Body>

      <Modal.Footer className="border-0 pt-0">
        <Button variant="outline-secondary" onClick={onHide} disabled={loading}>
          <IconX size={16} className="me-1" />
          Cancelar
        </Button>
        <Button variant="primary" onClick={handleSubmit} disabled={loading}>
          {loading ? (
            <>
              <Spinner size="sm" className="me-2" />
              Salvando...
            </>
          ) : (
            <>
              <IconCheck size={16} className="me-1" />
              Salvar Personalização
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default OrganizationBranding;